import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Users, AlertTriangle, Mail, Receipt, Megaphone, Gift, TrendingUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell, Legend } from "recharts";

const STATUS_LABEL = { received: "Rebuda", reviewing: "En revisió", in_progress: "En procés", resolved: "Resolta" };
const CAT_LABEL = { cleaning: "Neteja", urban_furniture: "Mobiliari", lighting: "Il·luminació", signage: "Senyalització", security: "Seguretat", other: "Altres" };
const COLORS = ["#CF0283", "#F59E0B", "#10B981", "#EF4444", "#3B82F6", "#8B5CF6"];

function KPI({ icon: Icon, label, value, tone = "primary", tid }) {
  const bg = tone === "accent" ? "bg-accent/15 text-accent" : tone === "success" ? "bg-emerald-500/15 text-emerald-600" : "bg-primary/10 text-primary";
  return (
    <Card className="rounded-2xl border-2" data-testid={tid}>
      <CardContent className="p-6 flex items-start gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${bg}`}><Icon className="w-5 h-5" /></div>
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
          <div className="font-heading font-bold text-2xl mt-1">{value}</div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/dashboard/stats").then(({ data }) => setStats(data)); }, []);
  if (!stats) return <div className="text-muted-foreground">Carregant…</div>;

  const statusData = Object.entries(stats.incidents.by_status).map(([k, v]) => ({ name: STATUS_LABEL[k] || k, value: v }));
  const catData = Object.entries(stats.incidents.by_category).map(([k, v]) => ({ name: CAT_LABEL[k] || k, value: v }));
  const mailings = stats.mailings || { top: [], avg_open_rate: 0, total_sent: 0, total_opens: 0 };
  const mailingChart = (mailings.top || []).map(m => ({
    name: (m.subject || "").length > 22 ? m.subject.slice(0, 20) + "…" : (m.subject || ""),
    open_rate: m.open_rate,
    sent: m.sent_count,
    opens: m.open_count,
  }));

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <div className="overline text-accent">Panell</div>
        <h1 className="mt-2 text-3xl sm:text-4xl font-heading font-bold tracking-tight">Vista general</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={Users} label="Associats" value={stats.associates.total} tid="kpi-associates" />
        <KPI icon={AlertTriangle} label="Incidències obertes" value={stats.incidents.open} tone="accent" tid="kpi-incidents" />
        <KPI icon={Mail} label="Mailings enviats" value={stats.mailings_sent} tid="kpi-mailings" />
        <KPI icon={Receipt} label="Factures pendents" value={stats.invoices.pending} tid="kpi-invoices" />
        <KPI icon={Megaphone} label="Campanyes actives" value={stats.campaigns_active} tone="success" tid="kpi-campaigns" />
        <KPI icon={Gift} label="Targetes actives" value={stats.gift_cards_active} tone="accent" tid="kpi-giftcards" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="rounded-2xl border-2">
          <CardContent className="p-6">
            <div className="font-heading font-semibold mb-4">Incidències per estat</div>
            <div className="h-64" data-testid="chart-incidents-status">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={statusData}>
                  <XAxis dataKey="name" fontSize={12} />
                  <YAxis allowDecimals={false} fontSize={12} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#CF0283" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-2">
          <CardContent className="p-6">
            <div className="font-heading font-semibold mb-4">Incidències per categoria</div>
            <div className="h-64" data-testid="chart-incidents-category">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={catData.filter((d) => d.value > 0)} dataKey="value" nameKey="name" outerRadius={80} label>
                    {catData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Legend />
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* MAILING ANALYTICS */}
      <div className="grid lg:grid-cols-3 gap-4" data-testid="mailing-analytics">
        <Card className="rounded-2xl border-2 lg:col-span-1">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center gap-2 font-heading font-semibold">
              <TrendingUp className="w-4 h-4 text-primary" aria-hidden="true" /> Analítica mailing
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Taxa d'obertura mitjana</div>
              <div className="font-heading font-bold text-4xl text-primary mt-1" data-testid="mailing-avg-open-rate">{mailings.avg_open_rate}%</div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="p-3 rounded-xl bg-secondary/60">
                <div className="text-xs text-muted-foreground">Enviats</div>
                <div className="font-heading font-bold text-lg" data-testid="mailing-total-sent">{mailings.total_sent}</div>
              </div>
              <div className="p-3 rounded-xl bg-secondary/60">
                <div className="text-xs text-muted-foreground">Oberts</div>
                <div className="font-heading font-bold text-lg" data-testid="mailing-total-opens">{mailings.total_opens}</div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Basat en els últims 10 mailings enviats.</p>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-2 lg:col-span-2">
          <CardContent className="p-6">
            <div className="font-heading font-semibold mb-4">Top mailings per obertura</div>
            <div className="h-64" data-testid="chart-mailings-open-rate">
              {mailingChart.length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Encara no s'ha enviat cap mailing.</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={mailingChart} layout="vertical" margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                    <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} fontSize={12} />
                    <YAxis type="category" dataKey="name" fontSize={11} width={140} />
                    <Tooltip formatter={(v, k) => k === "open_rate" ? `${v}%` : v} />
                    <Bar dataKey="open_rate" fill="#F59E0B" radius={[0, 8, 8, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
